import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  ExternalLink,
  FileText,
  Library,
  MessageSquare,
  PencilLine,
  Plus,
  RefreshCw,
  Send,
  Trash2,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import {
  EmptyState,
  PanelActionButton,
  SectionPanel,
  StatusPill,
  SummaryTile,
} from "@/components/platform/ContentPrimitives";
import { useAuth } from "@/contexts/AuthContext";
import { BACKEND_URL, api } from "@/lib/api";
import { formatDateTime, splitTags } from "@/lib/formatters";
import { PLATFORM_LINKS } from "@/lib/polenica";

const TAB_OPTIONS = [
  { id: "foro", label: "Foro", icon: MessageSquare },
  { id: "biblioteca", label: "Biblioteca", icon: Library },
];

const EMPTY_QUESTION_FORM = {
  titulo: "",
  descripcion: "",
  etiquetas: "",
};

const EMPTY_DOCUMENT_FORM = {
  titulo: "",
  descripcion: "",
  categoria: "otro",
  etiquetas: "",
  acepta_deslinde: false,
  archivo: null,
};

const DOCUMENT_CATEGORY_OPTIONS = [
  { value: "compostaje", label: "Compostaje" },
  { value: "cultivo", label: "Cultivo" },
  { value: "suelo", label: "Suelo y sustratos" },
  { value: "riego", label: "Riego" },
  { value: "semillas", label: "Semillas y propagacion" },
  { value: "bioinsumos", label: "Bioinsumos" },
  { value: "plagas", label: "Plagas y manejo" },
  { value: "paca_digestora", label: "Paca digestora" },
  { value: "organizacion", label: "Organizacion comunitaria" },
  { value: "normatividad", label: "Normatividad" },
  { value: "otro", label: "Otro" },
];

const VECTOR_STATUS_META = {
  queued: {
    label: "En cola",
    tone: "warning",
    helper: "El PDF fue recibido y pasara a lectura automatizada.",
  },
  extracting: {
    label: "Extrayendo",
    tone: "warning",
    helper: "El contenido del documento se esta preparando para indexacion.",
  },
  indexing: {
    label: "Indexando",
    tone: "warning",
    helper: "El documento se esta organizando para consulta posterior.",
  },
  ready: {
    label: "Listo",
    tone: "positive",
    helper: "Este recurso ya puede consultarse dentro del flujo documental de la plataforma.",
  },
  error: {
    label: "Con error",
    tone: "danger",
    helper: "La preparacion automatizada fallo. Revise el recurso o vuelva a cargarlo.",
  },
  legacy: {
    label: "Disponible",
    tone: "neutral",
    helper: "Este recurso pertenece al acervo actual de la biblioteca comunitaria.",
  },
};

function getVectorStatusMeta(status) {
  return VECTOR_STATUS_META[status] || VECTOR_STATUS_META.legacy;
}

function isVectorProcessing(status) {
  return status === "queued" || status === "extracting" || status === "indexing";
}

function getCategoryLabel(category) {
  return DOCUMENT_CATEGORY_OPTIONS.find((option) => option.value === category)?.label || category || "Otro";
}

function resolveDocumentUrl(document) {
  const candidate = document?.download_url || document?.archivo_url;
  if (!candidate) {
    return null;
  }
  if (/^https?:\/\//i.test(candidate)) {
    return candidate;
  }
  if (candidate.startsWith("/api/")) {
    return `${BACKEND_URL}${candidate}`;
  }
  return candidate;
}

function DocumentProgressBar({ value }) {
  const normalizedValue = Math.max(0, Math.min(Number(value) || 0, 100));
  return (
    <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-primary/10">
      <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${normalizedValue}%` }} />
    </div>
  );
}

function InputField({ label, children, hint }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-primary">{label}</span>
      {children}
      {hint ? <span className="mt-2 block text-xs text-mutedForeground">{hint}</span> : null}
    </label>
  );
}

function inputClass() {
  return "h-12 w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 outline-none transition focus:border-primary/35";
}

function textareaClass() {
  return "w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 py-3 outline-none transition focus:border-primary/35";
}

function ForumQuestionCard({ question, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(question.id)}
      className={`w-full rounded-[24px] border p-5 text-left transition ${
        selected ? "border-primary bg-primary text-primaryForeground" : "border-primary/10 bg-[#FAFBF7] text-textMain hover:border-primary/25"
      }`}
    >
      <div className="flex flex-wrap gap-2">
        <StatusPill tone={selected ? "neutral" : "default"}>{question.estado}</StatusPill>
        {(question.etiquetas || []).slice(0, 3).map((tag) => (
          <StatusPill key={tag} tone="neutral">
            {tag}
          </StatusPill>
        ))}
      </div>
      <h3 className="mt-4 font-heading text-2xl font-semibold">{question.titulo}</h3>
      <p className={`mt-2 text-sm leading-6 ${selected ? "text-primaryForeground/88" : "text-mutedForeground"}`}>
        {question.descripcion}
      </p>
      <div className={`mt-4 flex flex-wrap items-center justify-between gap-3 text-sm ${selected ? "text-primaryForeground/82" : "text-mutedForeground"}`}>
        <span>{question.autor?.nombre || "Usuario"}</span>
        <span>{question.respuestas_count || 0} respuestas</span>
      </div>
    </button>
  );
}

function updateSearchParams(setSearchParams, updater) {
  setSearchParams((current) => {
    const next = new URLSearchParams(current);
    updater(next);
    return next;
  });
}

export default function Knowledge() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "foro";

  const [activeTab, setActiveTab] = useState(initialTab);
  const [profileOverview, setProfileOverview] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionSearch, setQuestionSearch] = useState("");
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState(EMPTY_QUESTION_FORM);
  const [answerDraft, setAnswerDraft] = useState("");
  const [documents, setDocuments] = useState([]);
  const [documentSearch, setDocumentSearch] = useState("");
  const [documentForm, setDocumentForm] = useState(EMPTY_DOCUMENT_FORM);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const deferredQuestionSearch = useDeferredValue(questionSearch);
  const deferredDocumentSearch = useDeferredValue(documentSearch);

  const editQuestionId = searchParams.get("editQuestion");
  const editDocumentId = searchParams.get("editDocument");

  const fetchForum = useCallback(async () => {
    const response = await api.get("/forum/questions", {
      params: { search: deferredQuestionSearch || undefined },
    });
    setQuestions(response.data?.items || []);
  }, [deferredQuestionSearch]);

  const fetchLibrary = useCallback(async () => {
    const response = await api.get("/library", {
      params: { search: deferredDocumentSearch || undefined },
    });
    setDocuments(response.data?.items || []);
  }, [deferredDocumentSearch]);

  const fetchProfileOverview = useCallback(async () => {
    if (!user) {
      setProfileOverview(null);
      return;
    }

    const response = await api.get("/profile/overview");
    setProfileOverview(response.data);
  }, [user]);

  const fetchSelectedQuestion = useCallback(async (questionId) => {
    const response = await api.get(`/forum/questions/${questionId}`);
    setSelectedQuestion(response.data);
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchForum(), fetchLibrary(), fetchProfileOverview()]);
      if (selectedQuestionId) {
        await fetchSelectedQuestion(selectedQuestionId);
      }
    } catch (_error) {
      toast.error("No fue posible cargar el modulo de saberes vivos.");
    } finally {
      setLoading(false);
    }
  }, [fetchForum, fetchLibrary, fetchProfileOverview, fetchSelectedQuestion, selectedQuestionId]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    updateSearchParams(setSearchParams, (next) => {
      next.set("tab", activeTab);
    });
  }, [activeTab, setSearchParams]);

  useEffect(() => {
    if (!questions.length) {
      setSelectedQuestionId(null);
      setSelectedQuestion(null);
      return;
    }

    const exists = questions.some((item) => item.id === selectedQuestionId);
    if (!exists) {
      setSelectedQuestionId(questions[0].id);
    }
  }, [questions, selectedQuestionId]);

  useEffect(() => {
    if (!selectedQuestionId) {
      setSelectedQuestion(null);
      return;
    }

    fetchSelectedQuestion(selectedQuestionId).catch(() => {
      toast.error("No fue posible cargar el hilo seleccionado.");
    });
  }, [fetchSelectedQuestion, selectedQuestionId]);

  useEffect(() => {
    if (!editQuestionId || !profileOverview?.content?.forum_questions) {
      setQuestionForm(EMPTY_QUESTION_FORM);
      return;
    }

    const target = profileOverview.content.forum_questions.find((item) => String(item.id) === String(editQuestionId));
    if (target) {
      setQuestionForm({
        titulo: target.titulo || "",
        descripcion: target.descripcion || "",
        etiquetas: Array.isArray(target.etiquetas) ? target.etiquetas.join(", ") : "",
      });
      return;
    }

    api
      .get(`/forum/questions/${editQuestionId}`)
      .then((response) => {
        const question = response.data;
        setQuestionForm({
          titulo: question.titulo || "",
          descripcion: question.descripcion || "",
          etiquetas: Array.isArray(question.etiquetas) ? question.etiquetas.join(", ") : "",
        });
      })
      .catch(() => toast.error("No fue posible cargar la pregunta para editarla."));
  }, [editQuestionId, profileOverview]);

  useEffect(() => {
    if (!editDocumentId || !profileOverview?.content?.library_documents) {
      setDocumentForm(EMPTY_DOCUMENT_FORM);
      setUploadProgress(0);
      return;
    }

    const target = profileOverview.content.library_documents.find((item) => String(item.id) === String(editDocumentId));
    if (target) {
      setDocumentForm({
        titulo: target.titulo || "",
        descripcion: target.descripcion || "",
        categoria: target.categoria || "otro",
        etiquetas: Array.isArray(target.etiquetas) ? target.etiquetas.join(", ") : "",
        acepta_deslinde: Boolean(target.acepta_deslinde),
        archivo: null,
      });
      return;
    }

    api
      .get(`/library/${editDocumentId}`)
      .then((response) => {
        const document = response.data;
        setDocumentForm({
          titulo: document.titulo || "",
          descripcion: document.descripcion || "",
          categoria: document.categoria || "otro",
          etiquetas: Array.isArray(document.etiquetas) ? document.etiquetas.join(", ") : "",
          acepta_deslinde: Boolean(document.acepta_deslinde),
          archivo: null,
        });
      })
      .catch(() => toast.error("No fue posible cargar el documento para editarlo."));
  }, [editDocumentId, profileOverview]);

  const myQuestions = useMemo(() => profileOverview?.content?.forum_questions || [], [profileOverview]);
  const myAnswers = useMemo(() => profileOverview?.content?.forum_answers || [], [profileOverview]);
  const myDocuments = useMemo(() => profileOverview?.content?.library_documents || [], [profileOverview]);

  const summary = useMemo(
    () => ({
      forumQuestions: questions.length,
      libraryDocuments: documents.length,
      ownQuestions: myQuestions.length,
      ownDocuments: myDocuments.length,
    }),
    [documents.length, myDocuments.length, myQuestions.length, questions.length],
  );

  const hasVectorProcessing = useMemo(
    () =>
      [...documents, ...myDocuments].some((document) => isVectorProcessing(document.vector_ingestion_status)),
    [documents, myDocuments],
  );

  useEffect(() => {
    if (!hasVectorProcessing || editDocumentId || editQuestionId) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      refreshAll().catch(() => undefined);
    }, 15000);

    return () => window.clearInterval(intervalId);
  }, [hasVectorProcessing, refreshAll, editDocumentId, editQuestionId]);

  const handleQuestionSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        titulo: questionForm.titulo,
        descripcion: questionForm.descripcion,
        etiquetas: splitTags(questionForm.etiquetas),
      };

      if (editQuestionId) {
        await api.put(`/forum/questions/${editQuestionId}`, payload);
        toast.success("Pregunta actualizada correctamente.");
      } else {
        await api.post("/forum/questions", payload);
        toast.success("Pregunta creada y publicada correctamente.");
      }

      setQuestionForm(EMPTY_QUESTION_FORM);
      updateSearchParams(setSearchParams, (next) => {
        next.delete("editQuestion");
      });
      await refreshAll();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No fue posible guardar la pregunta.");
    } finally {
      setSaving(false);
    }
  };

  const handleQuestionDelete = async (questionId) => {
    try {
      await api.delete(`/forum/questions/${questionId}`);
      toast.success("Pregunta retirada.");
      await refreshAll();
    } catch (_error) {
      toast.error("No fue posible eliminar la pregunta.");
    }
  };

  const handleAnswerSubmit = async (event) => {
    event.preventDefault();
    if (!selectedQuestionId) {
      return;
    }

    setSaving(true);
    try {
      await api.post(`/forum/questions/${selectedQuestionId}/answers`, { contenido: answerDraft });
      setAnswerDraft("");
      toast.success("Respuesta publicada.");
      await refreshAll();
      await fetchSelectedQuestion(selectedQuestionId);
    } catch (_error) {
      toast.error("No fue posible publicar la respuesta.");
    } finally {
      setSaving(false);
    }
  };

  const handleAnswerEdit = async (answerId, currentContent) => {
    const nextContent = window.prompt("Edite su respuesta:", currentContent || "");
    if (!nextContent || nextContent === currentContent) {
      return;
    }

    try {
      await api.put(`/forum/answers/${answerId}`, { contenido: nextContent });
      toast.success("Respuesta actualizada.");
      await refreshAll();
      if (selectedQuestionId) {
        await fetchSelectedQuestion(selectedQuestionId);
      }
    } catch (_error) {
      toast.error("No fue posible actualizar la respuesta.");
    }
  };

  const handleAnswerDelete = async (answerId) => {
    try {
      await api.delete(`/forum/answers/${answerId}`);
      toast.success("Respuesta retirada.");
      await refreshAll();
      if (selectedQuestionId) {
        await fetchSelectedQuestion(selectedQuestionId);
      }
    } catch (_error) {
      toast.error("No fue posible eliminar la respuesta.");
    }
  };

  const handleDocumentSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setUploadProgress(0);

    try {
      if (editDocumentId) {
        await api.put(`/library/${editDocumentId}`, {
          titulo: documentForm.titulo,
          descripcion: documentForm.descripcion,
          categoria: documentForm.categoria,
          etiquetas: splitTags(documentForm.etiquetas),
        });
        toast.success("Documento actualizado correctamente.");
      } else {
        if (!documentForm.archivo) {
          toast.error("Seleccione un PDF antes de cargar el documento.");
          return;
        }

        const formData = new FormData();
        formData.append("titulo", documentForm.titulo);
        formData.append("descripcion", documentForm.descripcion || "");
        formData.append("categoria", documentForm.categoria);
        formData.append("etiquetas", documentForm.etiquetas || "");
        formData.append("acepta_deslinde", String(documentForm.acepta_deslinde));
        formData.append("archivo", documentForm.archivo);

        await api.post("/library/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            if (!progressEvent.total) {
              return;
            }
            setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          },
        });
        toast.success("PDF cargado. La preparacion del documento continuara en segundo plano y puede tardar hasta 3 horas.");
      }

      setDocumentForm(EMPTY_DOCUMENT_FORM);
      setUploadProgress(0);
      updateSearchParams(setSearchParams, (next) => {
        next.delete("editDocument");
      });
      await refreshAll();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No fue posible guardar el documento.");
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentDelete = async (documentId) => {
    try {
      await api.delete(`/library/${documentId}`);
      toast.success("Documento retirado.");
      await refreshAll();
    } catch (_error) {
      toast.error("No fue posible eliminar el documento.");
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="platform-hero-banner rounded-[34px] p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/70">
              Saberes vivos
            </p>
            <h1 className="mt-3 flex items-center gap-3 font-heading text-4xl font-semibold text-primary sm:text-5xl">
              <BookOpen className="h-10 w-10" />
              Foro y biblioteca comunitaria
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-mutedForeground">
              Ponga preguntas en circulacion, recorra conversaciones del territorio y consulte documentos compartidos por la red.
            </p>
          </div>

          <PanelActionButton tone="secondary" onClick={refreshAll}>
            <RefreshCw className="h-4 w-4" />
            Actualizar
          </PanelActionButton>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-4">
          <SummaryTile
            icon={MessageSquare}
            label="Preguntas visibles"
            value={summary.forumQuestions}
            description="Hilos disponibles para lectura publica."
          />
          <SummaryTile
            icon={Library}
            label="Documentos"
            value={summary.libraryDocuments}
            description="Recursos visibles en la biblioteca."
          />
          <SummaryTile
            icon={FileText}
            label="Mis preguntas"
            value={summary.ownQuestions}
            description="Aportes propios dentro del foro."
          />
          <SummaryTile
            icon={BookOpen}
            label="Mis documentos"
            value={summary.ownDocuments}
            description="Recursos propios publicados y disponibles para la comunidad."
          />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        {TAB_OPTIONS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 rounded-full border px-5 py-3 text-sm font-semibold transition ${
                active ? "border-primary bg-primary text-primaryForeground" : "border-primary/12 bg-[#EDF5E6] text-primary"
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {!loading && activeTab === "foro" ? (
        <div className="mt-6 grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
          <SectionPanel
            title="Foro comunitario"
            eyebrow="Consulta abierta"
            description={
              <>
                Conversaciones, preguntas y aprendizajes que circulan en la red. Revise tambien las{" "}
                <a
                  href={PLATFORM_LINKS.forumConditions}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-primary underline underline-offset-4"
                >
                  condiciones del foro
                </a>
                .
              </>
            }
          >
            <InputField label="Buscar preguntas">
              <input
                value={questionSearch}
                onChange={(event) => setQuestionSearch(event.target.value)}
                className={inputClass()}
                placeholder="compostaje, semillas, pacas..."
              />
            </InputField>

            <div className="mt-6 space-y-4">
              {!questions.length ? (
                <EmptyState
                  icon={MessageSquare}
                  title="No hay preguntas publicadas"
                  description="Aun no existen hilos visibles para esta consulta."
                />
              ) : (
                questions.map((question) => (
                  <ForumQuestionCard
                    key={question.id}
                    question={question}
                    selected={selectedQuestionId === question.id}
                    onSelect={setSelectedQuestionId}
                  />
                ))
              )}
            </div>
          </SectionPanel>

          <div className="space-y-6">
            <SectionPanel
              title={selectedQuestion?.titulo || "Hilo seleccionado"}
              eyebrow="Detalle del hilo"
              description={
                selectedQuestion
                  ? "Lea la conversacion completa y, si tiene cuenta, deje su respuesta."
                  : "Seleccione una pregunta del listado para abrir el hilo."
              }
            >
              {!selectedQuestion ? (
                <EmptyState
                  icon={MessageSquare}
                  title="Sin hilo seleccionado"
                  description="Abra una pregunta del listado para revisar sus respuestas."
                />
              ) : (
                <div className="space-y-5">
                  <div className="flex flex-wrap gap-2">
                    <StatusPill>{selectedQuestion.estado}</StatusPill>
                    {(selectedQuestion.etiquetas || []).map((tag) => (
                      <StatusPill key={tag} tone="neutral">
                        {tag}
                      </StatusPill>
                    ))}
                  </div>

                  <div className="platform-soft-panel rounded-[24px] border border-primary/10 p-5">
                    <p className="text-sm leading-7 text-textMain">{selectedQuestion.descripcion}</p>
                    <p className="mt-4 text-xs text-mutedForeground">
                      {selectedQuestion.autor?.nombre || "Usuario"} · {formatDateTime(selectedQuestion.created_at)}
                    </p>
                  </div>

                  <div className="space-y-3">
                    {(selectedQuestion.respuestas || []).length ? (
                      selectedQuestion.respuestas.map((answer) => (
                        <article key={answer.id} className="platform-elevated-surface rounded-[22px] border p-4">
                          <p className="text-sm leading-6 text-textMain">{answer.contenido}</p>
                          <p className="mt-3 text-xs text-mutedForeground">
                            {answer.autor?.nombre || "Usuario"} · {formatDateTime(answer.created_at)}
                          </p>
                        </article>
                      ))
                    ) : (
                      <div className="platform-soft-panel rounded-[22px] border border-primary/10 px-4 py-6 text-sm text-mutedForeground">
                        Aun no hay respuestas visibles en este hilo.
                      </div>
                    )}
                  </div>

                  {!user ? (
                    <div className="platform-soft-panel rounded-[22px] border border-primary/10 px-4 py-4 text-sm leading-6 text-mutedForeground">
                      Puede leer el foro sin registrarse. Para preguntar, responder o gestionar sus aportes,{" "}
                      <Link to="/login" className="font-semibold text-primary underline underline-offset-4">
                        ingrese con su cuenta
                      </Link>
                      .
                    </div>
                  ) : (
                    <form className="space-y-3" onSubmit={handleAnswerSubmit}>
                      <InputField label="Responder">
                        <textarea
                          value={answerDraft}
                          onChange={(event) => setAnswerDraft(event.target.value)}
                          rows={4}
                          className={textareaClass()}
                          placeholder="Comparta su experiencia, recomendacion o respuesta."
                        />
                      </InputField>
                      <PanelActionButton type="submit" disabled={saving || !answerDraft.trim()}>
                        <Send className="h-4 w-4" />
                        Publicar respuesta
                      </PanelActionButton>
                    </form>
                  )}
                </div>
              )}
            </SectionPanel>

            <SectionPanel
              title={editQuestionId ? "Editar pregunta" : "Crear pregunta"}
              eyebrow="Gestion propia"
              description="Ponga su pregunta en circulacion y reciba respuestas de la comunidad polenica."
            >
              {!user ? (
                <EmptyState
                  icon={MessageSquare}
                  title="Puede leer el foro sin registrarse"
                  description="Para preguntar, responder o gestionar sus aportes, ingrese con su cuenta."
                />
              ) : (
                <form className="space-y-4" onSubmit={handleQuestionSubmit}>
                  <InputField label="Titulo">
                    <input
                      value={questionForm.titulo}
                      onChange={(event) => setQuestionForm((current) => ({ ...current, titulo: event.target.value }))}
                      className={inputClass()}
                      required
                    />
                  </InputField>

                  <InputField label="Descripcion">
                    <textarea
                      value={questionForm.descripcion}
                      onChange={(event) => setQuestionForm((current) => ({ ...current, descripcion: event.target.value }))}
                      rows={4}
                      className={textareaClass()}
                      required
                    />
                  </InputField>

                  <InputField label="Etiquetas" hint="Separe las etiquetas con coma.">
                    <input
                      value={questionForm.etiquetas}
                      onChange={(event) => setQuestionForm((current) => ({ ...current, etiquetas: event.target.value }))}
                      className={inputClass()}
                    />
                  </InputField>

                  <div className="flex flex-wrap gap-3">
                    <PanelActionButton type="submit" disabled={saving}>
                      <Plus className="h-4 w-4" />
                      {editQuestionId ? "Actualizar pregunta" : "Publicar pregunta"}
                    </PanelActionButton>
                    {editQuestionId ? (
                      <PanelActionButton
                        tone="secondary"
                        onClick={() =>
                          updateSearchParams(setSearchParams, (next) => {
                            next.delete("editQuestion");
                          })
                        }
                      >
                        Cancelar
                      </PanelActionButton>
                    ) : null}
                  </div>
                </form>
              )}
            </SectionPanel>

            <SectionPanel
              title="Mis aportes al foro"
              eyebrow="Seguimiento"
              description="Revise preguntas propias y respuestas publicadas desde su cuenta."
            >
              {!user ? (
                <EmptyState
                  icon={MessageSquare}
                  title="Sin gestion activa"
                  description="Ingrese con su cuenta para revisar sus preguntas y respuestas."
                />
              ) : (
                <div className="space-y-5">
                  <div className="space-y-4">
                    {myQuestions.length ? (
                      myQuestions.map((question) => (
                        <article key={question.id} className="rounded-[22px] border border-primary/10 bg-[#FAFBF7] p-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <div className="flex flex-wrap gap-2">
                                <StatusPill
                                  tone={
                                    question.estado === "PUBLICADO"
                                      ? "positive"
                                      : question.estado === "RECHAZADO"
                                        ? "danger"
                                        : "warning"
                                  }
                                >
                                  {question.estado}
                                </StatusPill>
                              </div>
                              <h3 className="mt-3 font-heading text-xl font-semibold text-primary">
                                {question.titulo}
                              </h3>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <PanelActionButton
                                tone="secondary"
                                onClick={() =>
                                  updateSearchParams(setSearchParams, (next) => {
                                    next.set("tab", "foro");
                                    next.set("editQuestion", String(question.id));
                                  })
                                }
                              >
                                <PencilLine className="h-4 w-4" />
                                Editar
                              </PanelActionButton>
                              <PanelActionButton tone="danger" onClick={() => handleQuestionDelete(question.id)}>
                                <Trash2 className="h-4 w-4" />
                                Eliminar
                              </PanelActionButton>
                            </div>
                          </div>
                        </article>
                      ))
                    ) : (
                      <p className="text-sm text-mutedForeground">Aun no ha creado preguntas.</p>
                    )}
                  </div>

                  <div className="space-y-4">
                    {myAnswers.length ? (
                      myAnswers.map((answer) => (
                        <article key={answer.id} className="rounded-[22px] border border-primary/10 bg-[#FAFBF7] p-4">
                          <p className="text-sm leading-6 text-textMain">{answer.contenido}</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <PanelActionButton tone="secondary" onClick={() => handleAnswerEdit(answer.id, answer.contenido)}>
                              <PencilLine className="h-4 w-4" />
                              Editar
                            </PanelActionButton>
                            <PanelActionButton tone="danger" onClick={() => handleAnswerDelete(answer.id)}>
                              <Trash2 className="h-4 w-4" />
                              Eliminar
                            </PanelActionButton>
                          </div>
                        </article>
                      ))
                    ) : (
                      <p className="text-sm text-mutedForeground">Aun no ha respondido preguntas.</p>
                    )}
                  </div>
                </div>
              )}
            </SectionPanel>
          </div>
        </div>
      ) : null}

      {!loading && activeTab === "biblioteca" ? (
        <div className="mt-6 grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
          <SectionPanel
            id="biblioteca-comunitaria"
            title="Biblioteca comunitaria"
            eyebrow="Consulta abierta"
            description={
              <>
                Consulte documentos, guias y materiales compartidos por la red. Revise tambien las{" "}
                <a
                  href={PLATFORM_LINKS.libraryConditions}
                  target={PLATFORM_LINKS.libraryConditions.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className="font-semibold text-primary underline underline-offset-4"
                >
                  condiciones de la biblioteca
                </a>
                .
              </>
            }
          >
            <InputField label="Buscar documentos">
              <input
                value={documentSearch}
                onChange={(event) => setDocumentSearch(event.target.value)}
                className={inputClass()}
                placeholder="sustratos, siembra, plagas..."
              />
            </InputField>

            <div className="mt-6 space-y-4">
              {!documents.length ? (
                <EmptyState
                  icon={Library}
                  title="No hay documentos visibles"
                  description="Aun no hay recursos publicados para esta consulta."
                />
              ) : (
                documents.map((document) => {
                  const vectorMeta = getVectorStatusMeta(document.vector_ingestion_status);
                  const documentUrl = resolveDocumentUrl(document);

                  return (
                    <article key={document.id} className="rounded-[24px] border border-primary/10 bg-[#FAFBF7] p-5">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap gap-2">
                            <StatusPill>{document.estado}</StatusPill>
                            <StatusPill tone="neutral">{getCategoryLabel(document.categoria)}</StatusPill>
                            <StatusPill tone={vectorMeta.tone}>{vectorMeta.label}</StatusPill>
                          </div>
                          <h3 className="mt-4 font-heading text-2xl font-semibold text-primary">{document.titulo}</h3>
                          <p className="mt-2 text-sm leading-6 text-mutedForeground">
                            {document.descripcion || "Sin descripcion."}
                          </p>
                          <p className="mt-3 text-sm text-mutedForeground">{vectorMeta.helper}</p>
                          {isVectorProcessing(document.vector_ingestion_status) ? (
                            <>
                              <DocumentProgressBar value={document.vector_ingestion_progress} />
                              <p className="mt-2 text-xs text-mutedForeground">
                                {document.vector_ingestion_eta_message || "Este PDF seguira procesandose en segundo plano."}
                              </p>
                            </>
                          ) : null}
                        </div>

                        {documentUrl && document.archivo_disponible ? (
                          <a
                            href={documentUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 rounded-2xl border border-primary/15 bg-white px-4 py-3 text-sm font-semibold text-primary"
                          >
                            Abrir recurso
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        ) : (
                          <button
                            type="button"
                            disabled
                            className="inline-flex cursor-not-allowed items-center gap-2 rounded-2xl border border-border bg-[#EEF0EA] px-4 py-3 text-sm font-semibold text-mutedForeground opacity-70"
                            title="El archivo de este recurso no está disponible"
                          >
                            Recurso no disponible
                            <ExternalLink className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })
              )}
            </div>
          </SectionPanel>

          <div className="space-y-6">
            <SectionPanel
              title={editDocumentId ? "Editar documento" : "Cargar documento"}
              eyebrow="Gestion propia"
              description="Suba PDFs por categoria, deje claros sus metadatos y permita que el sistema prepare el recurso para consulta posterior."
            >
              {!user ? (
                <EmptyState
                  icon={Library}
                  title="Puede consultar la biblioteca comunitaria sin iniciar sesion"
                  description="Para publicar documentos, ingrese con su cuenta."
                />
              ) : (
                <form className="space-y-4" onSubmit={handleDocumentSubmit}>
                  <InputField label="Titulo">
                    <input
                      value={documentForm.titulo}
                      onChange={(event) => setDocumentForm((current) => ({ ...current, titulo: event.target.value }))}
                      className={inputClass()}
                      required
                    />
                  </InputField>

                  <InputField label="Descripcion">
                    <textarea
                      value={documentForm.descripcion}
                      onChange={(event) => setDocumentForm((current) => ({ ...current, descripcion: event.target.value }))}
                      rows={4}
                      className={textareaClass()}
                    />
                  </InputField>

                  <InputField label="Categoria">
                    <select
                      value={documentForm.categoria}
                      onChange={(event) => setDocumentForm((current) => ({ ...current, categoria: event.target.value }))}
                      className={inputClass()}
                      required
                    >
                      {DOCUMENT_CATEGORY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </InputField>

                  <InputField label="Etiquetas" hint="Separe las etiquetas con coma.">
                    <input
                      value={documentForm.etiquetas}
                      onChange={(event) => setDocumentForm((current) => ({ ...current, etiquetas: event.target.value }))}
                      className={inputClass()}
                    />
                  </InputField>

                  {!editDocumentId ? (
                    <InputField
                      label="PDF"
                      hint="Solo se aceptan archivos PDF. El procesamiento del documento puede tardar hasta 3 horas."
                    >
                      <input
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={(event) =>
                          setDocumentForm((current) => ({
                            ...current,
                            archivo: event.target.files?.[0] || null,
                          }))
                        }
                        className="block w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 py-3 text-sm text-textMain"
                        required
                      />
                    </InputField>
                  ) : (
                    <div className="rounded-[22px] border border-primary/10 bg-[#FAFBF7] px-4 py-4 text-sm leading-6 text-mutedForeground">
                      En esta vista la edicion actualiza metadatos del recurso y lo mantiene publicado.
                    </div>
                  )}

                  <label className="flex items-start gap-3 rounded-[22px] border border-primary/10 bg-[#FAFBF7] px-4 py-4">
                    <input
                      type="checkbox"
                      checked={documentForm.acepta_deslinde}
                      onChange={(event) =>
                        setDocumentForm((current) => ({ ...current, acepta_deslinde: event.target.checked }))
                      }
                      className="mt-1 h-5 w-5 rounded border-primary text-primary"
                    />
                    <span className="text-sm leading-6 text-textMain">
                      Acepto el deslinde de responsabilidad para el contenido compartido en la biblioteca y su procesamiento automatizado.
                    </span>
                  </label>

                  {!editDocumentId && uploadProgress > 0 ? (
                    <div className="rounded-[22px] border border-primary/10 bg-[#FAFBF7] p-4">
                      <div className="flex items-center justify-between gap-3 text-sm text-textMain">
                        <span>Subiendo PDF al backend</span>
                        <span className="font-semibold text-primary">{uploadProgress}%</span>
                      </div>
                      <DocumentProgressBar value={uploadProgress} />
                    </div>
                  ) : null}

                  <div className="flex flex-wrap gap-3">
                    <PanelActionButton type="submit" disabled={saving}>
                      <Plus className="h-4 w-4" />
                      {editDocumentId ? "Actualizar documento" : "Cargar PDF"}
                    </PanelActionButton>
                    {editDocumentId ? (
                      <PanelActionButton
                        tone="secondary"
                        onClick={() =>
                          updateSearchParams(setSearchParams, (next) => {
                            next.delete("editDocument");
                          })
                        }
                      >
                        Cancelar
                      </PanelActionButton>
                    ) : null}
                  </div>
                </form>
              )}
            </SectionPanel>

            <SectionPanel
              id="biblioteca-mis-documentos"
              title="Mis documentos"
              eyebrow="Seguimiento"
              description="Revise el estado de sus recursos y vuelva a editarlos cuando necesite ajustar la informacion."
            >
              {!user ? (
                <EmptyState
                  icon={FileText}
                  title="Puede consultar la biblioteca comunitaria sin iniciar sesion"
                  description="Para publicar documentos, ingrese con su cuenta."
                />
              ) : !myDocuments.length ? (
                <EmptyState
                  icon={FileText}
                  title="Aun no ha cargado documentos"
                  description="Suba su primer recurso para compartir conocimiento con la red."
                />
              ) : (
                <div className="space-y-4">
                  {myDocuments.map((document) => {
                    const vectorMeta = getVectorStatusMeta(document.vector_ingestion_status);
                    const documentUrl = resolveDocumentUrl(document);

                    return (
                      <article key={document.id} className="rounded-[22px] border border-primary/10 bg-[#FAFBF7] p-4">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap gap-2">
                              <StatusPill
                                tone={
                                  document.estado === "PUBLICADO"
                                    ? "positive"
                                    : document.estado === "RECHAZADO"
                                      ? "danger"
                                      : "warning"
                                }
                              >
                                {document.estado}
                              </StatusPill>
                              <StatusPill tone="neutral">{getCategoryLabel(document.categoria)}</StatusPill>
                              <StatusPill tone={vectorMeta.tone}>{vectorMeta.label}</StatusPill>
                            </div>
                            <h4 className="mt-3 font-heading text-lg font-semibold text-primary">{document.titulo}</h4>
                            <p className="mt-2 text-sm text-mutedForeground">
                              {document.nombre_archivo_original || "Recurso heredado del flujo anterior"}
                            </p>
                            <p className="mt-2 text-sm leading-6 text-mutedForeground">
                              {document.vector_ingestion_error || document.vector_ingestion_eta_message || vectorMeta.helper}
                            </p>
                            {isVectorProcessing(document.vector_ingestion_status) ? (
                              <DocumentProgressBar value={document.vector_ingestion_progress} />
                            ) : null}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {documentUrl ? (
                              <PanelActionButton tone="secondary" onClick={() => window.open(documentUrl, "_blank", "noopener,noreferrer")}>
                                <ExternalLink className="h-4 w-4" />
                                Abrir
                              </PanelActionButton>
                            ) : null}
                            <PanelActionButton
                              tone="secondary"
                              onClick={() =>
                                updateSearchParams(setSearchParams, (next) => {
                                  next.set("tab", "biblioteca");
                                  next.set("editDocument", String(document.id));
                                })
                              }
                            >
                              <PencilLine className="h-4 w-4" />
                              Editar
                            </PanelActionButton>
                            <PanelActionButton tone="danger" onClick={() => handleDocumentDelete(document.id)}>
                              <Trash2 className="h-4 w-4" />
                              Eliminar
                            </PanelActionButton>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </SectionPanel>
          </div>
        </div>
      ) : null}
    </div>
  );
}
